/**
 * Buyer Registration & Starred Offers API Service
 */

import { apiClient, handleApiError } from './client';

export interface BuyerRegistrationResponse {
  success: boolean;
  message: string;
  uid: string;
  data: {
    email: string;
    firstName: string;
    tier: string;
  };
}

interface BuyerFormData {
  firstName: string;
  lastName: string;
  email: string;
  motDePasse: string;
  telephone: string;
  adresseRue: string;
  ville: string;
  province: string;
  codePostal: string;
  pays: string;
  sourcesMarketing: string[];
  secteurs: Array<{
    secteur: string;
    anneesExperience: string;
    licencePermis: string;
    juridictionsCanada: string[];
    juridictionsUSA: string[];
  }>;
  selfDescription: string;
  experience: string;
}

export interface StarredOffer {
  id: string;
  buyerId: string;
  offerId: string;
  dealId: string;
  offerSnapshot: {
    loanAmount: number;
    interestRate: number;
    termMonths: number;
    loanType: string | null;
    dealName: string | null;
    isAnonymous: boolean;
    isByod: boolean;
    lenderName?: string;
    lenderCompany?: string;
    calendarLink?: string;
  };
  createdAt: string;
}

/**
 * Maps frontend form data to the backend DTO format
 */
function toRegistrationPayload(formData: BuyerFormData, tier: string, language: string) {
  return {
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    password: formData.motDePasse,
    phone: formData.telephone,

    // Address
    streetAddress: formData.adresseRue,
    city: formData.ville,
    stateProvince: formData.province,
    zipPostalCode: formData.codePostal,
    country: formData.pays,

    // Marketing & Profile
    howHeard: formData.sourcesMarketing,
    selfDescription: formData.selfDescription,
    experience: formData.experience,

    // Sectors of interest
    sectors: formData.secteurs.map(s => ({
      sector: s.secteur,
      yearsOfExperience: s.anneesExperience,
      licensePermit: s.licencePermis,
      jurisdictionsCanada: s.juridictionsCanada,
      jurisdictionsUSA: s.juridictionsUSA,
    })),

    // Tier & compliance
    tier,
    language,
  };
}

export const buyerApi = {
  /**
   * Register a new buyer via POST /buyer/registration
   */
  async register(formData: BuyerFormData, tier: string, language: string = 'en'): Promise<BuyerRegistrationResponse> {
    const payload = toRegistrationPayload(formData, tier, language);
    const response = await apiClient.post<BuyerRegistrationResponse>(
      '/buyer/registration',
      payload,
    );
    return response.data;
  },

  /**
   * Star (save) a financing offer
   */
  async starOffer(offerId: string, dealId: string) {
    const response = await apiClient.post('/buyer/starred-offers', { offerId, dealId });
    return response.data;
  },

  /**
   * Unstar (remove) a financing offer
   */
  async unstarOffer(offerId: string) {
    const response = await apiClient.delete(`/buyer/starred-offers/${offerId}`);
    return response.data;
  },

  /**
   * Get all starred offers for the buyer's dashboard
   */
  async getStarredOffers(): Promise<StarredOffer[]> {
    const response = await apiClient.get<StarredOffer[]>('/buyer/starred-offers');
    return response.data;
  },
};

export { handleApiError };
